/**
 * Fabric (decimal template): per-meter price = unit price × 4; quantity UI in meters, cart stores quarter-meter units.
 * UI starts at 1 m and the +/- buttons step by 0.25 m (1, 1.25, 1.5, 1.75, 2 ...).
 * Fractional lengths (0.25 / 0.5 / 0.75) remain typeable down to the variant minimum.
 *
 * NOTE: the quantity input lives outside the <form> and is linked via the `form` attribute,
 * so form.querySelectorAll() will not see it — always resolve inputs through formFabricInputs().
 */
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { formatMoney, debounce } from '@theme/utilities';

const CHUNK = 0.25;
const CHUNKS_PER_METER = 4;
const BTN_STEP = CHUNK; // +/- steps 0.25 m: 1, 1.25, 1.5, 1.75, 2 ...
const DEFAULT_START = 1;
const EPS = 1e-9;

function fabricMultiplier(form) {
  const m = parseInt(form?.dataset?.tradeunoFabricChunksPerMeter || '1', 10);
  return m >= CHUNKS_PER_METER ? m : 1;
}

/**
 * Collects matching inputs both inside the form and associated to it via the `form` attribute.
 * form.elements includes form-associated controls that live elsewhere in the DOM.
 */
function formFabricInputs(form, selector, datasetFlag) {
  const found = new Set();
  form.querySelectorAll(selector).forEach((el) => found.add(el));
  Array.from(form.elements || []).forEach((el) => {
    if (!(el instanceof HTMLInputElement)) return;
    if (datasetFlag ? el.dataset[datasetFlag] === 'true' : el.matches(selector)) found.add(el);
  });
  return Array.from(found);
}

function formatMeterDisplay(m) {
  const x = Math.round(Math.max(0, m) * 100) / 100;
  if (Number.isInteger(x)) return String(x);
  const s = x.toFixed(2);
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function roundMeters(raw, minM, maxM) {
  let n = parseFloat(raw);
  if (Number.isNaN(n)) n = minM;
  n = Math.round(n / CHUNK) * CHUNK;
  if (n < minM) n = minM;
  if (n > maxM) n = maxM;
  return Math.round(n * 100) / 100;
}

/** Preferred opening quantity (1 m), clamped into the variant's allowed range. */
function startMeters(minM, maxM) {
  return Math.min(Math.max(DEFAULT_START, minM), maxM);
}

function metersToChunks(meters, minChunks) {
  const c = Math.round(meters / CHUNK);
  return Math.max(minChunks, c);
}

function isFabricCartUpdatesInput(inp) {
  return (
    inp instanceof HTMLInputElement &&
    inp.name === 'updates[]' &&
    inp.dataset.tradeunoMeterQty === 'true' &&
    inp.dataset.index
  );
}

/** Mini-cart / cart page: theme CartItemsComponent only reacts to QuantitySelectorUpdateEvent, not hdt-quantity-wrapp. */
function dispatchFabricCartLineQty(inp) {
  if (!isFabricCartUpdatesInput(inp)) return;
  const line = Number(inp.dataset.index);
  if (!line || Number.isNaN(line)) return;
  const minChunks = parseInt(inp.dataset.tradeunoMinChunks || '1', 10);
  const meters = parseFloat(inp.value);
  const chunks = metersToChunks(Number.isNaN(meters) ? minChunks * CHUNK : meters, minChunks);
  inp.dispatchEvent(new QuantitySelectorUpdateEvent(chunks, line));
}

function syncFabricQtyButtons(inp) {
  const wrap = inp.closest('[data-tradeuno-fabric-qty]');
  if (!wrap) return;
  const minM = parseFloat(inp.getAttribute('min')) || CHUNK;
  const maxM = parseFloat(inp.getAttribute('max'));
  const maxOk = !Number.isNaN(maxM);
  const v = parseFloat(inp.value) || minM;
  const minus = wrap.querySelector('.tradeuno-fabric-qty__minus');
  const plus = wrap.querySelector('.tradeuno-fabric-qty__plus');
  if (minus) minus.classList.toggle('disabled', v <= minM + EPS);
  if (plus) plus.classList.toggle('disabled', maxOk && v >= maxM - EPS);
}

/** dir: +1 (plus) or -1 (minus). Steps a whole meter at a time, snapping off-grid values to whole meters. */
function onFabricQtyStep(inp, dir) {
  const minM = parseFloat(inp.getAttribute('min')) || CHUNK;
  let maxM = parseFloat(inp.getAttribute('max'));
  if (Number.isNaN(maxM)) maxM = 1e9;
  const cur = parseFloat(inp.value) || startMeters(minM, maxM);
  const next = roundMeters(cur + dir * BTN_STEP, minM, maxM);
  inp.value = formatMeterDisplay(next);
  syncFabricQtyButtons(inp);
  if (isFabricCartUpdatesInput(inp)) {
    dispatchFabricCartLineQty(inp);
  }
}

// Interactions use delegated document listeners (below) so both the PDP control and
// the cart-drawer control keep working after the mini-cart re-renders its line items.
// Here we only set the initial +/- disabled state.
function bindFabricQtyContainer(container) {
  const inp = container.querySelector('.tradeuno-fabric-qty__input');
  if (inp) syncFabricQtyButtons(inp);
}

const cartDispatchDebounced = new WeakMap();
function normalizeFabricInput(inp) {
  const minM = parseFloat(inp.getAttribute('min')) || CHUNK;
  let maxM = parseFloat(inp.getAttribute('max'));
  if (Number.isNaN(maxM)) maxM = 1e9;
  inp.value = formatMeterDisplay(roundMeters(inp.value, minM, maxM));
  syncFabricQtyButtons(inp);
  if (isFabricCartUpdatesInput(inp)) {
    let d = cartDispatchDebounced.get(inp);
    if (!d) {
      d = debounce(() => dispatchFabricCartLineQty(inp), 280);
      cartDispatchDebounced.set(inp, d);
    }
    d();
  }
}

// Delegated: works for PDP + freshly re-rendered cart-drawer lines.
document.addEventListener('click', (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-tradeuno-fabric-step]') : null;
  if (!btn) return;
  e.preventDefault();
  const wrap = btn.closest('[data-tradeuno-fabric-qty]');
  const inp = wrap && wrap.querySelector('.tradeuno-fabric-qty__input');
  if (inp) onFabricQtyStep(inp, btn.getAttribute('data-tradeuno-fabric-step') === 'plus' ? 1 : -1);
});
['change', 'focusout'].forEach((evt) => {
  document.addEventListener(evt, (e) => {
    const inp = e.target instanceof Element ? e.target.closest('.tradeuno-fabric-qty__input') : null;
    if (inp) normalizeFabricInput(inp);
  });
});

function patchFabricPrices(form, variant) {
  const mult = fabricMultiplier(form);
  if (mult <= 1 || !variant) return;
  const fmt = window.themeHDN?.settings?.currencyFormat || '';

  form.querySelectorAll('hdt-compare-at-price').forEach((el) => {
    const onSale = variant.compare_at_price > variant.price;
    el.hidden = !onSale;
    if (!onSale) return;
    const money = el.querySelector('.hdt-money');
    if (money) {
      money.outerHTML = `<span class="hdt-money">${formatMoney(variant.compare_at_price * mult, fmt)}</span>`;
    }
  });

  form.querySelectorAll('hdt-price').forEach((el) => {
    if (el.tagName.toLowerCase() === 'hdt-unit-price' || el.classList.contains('hdt-unit-price')) return;
    const money = el.querySelector('.hdt-money');
    if (money) {
      money.outerHTML = `<span class="hdt-money">${formatMoney(variant.price * mult, fmt)}</span>`;
    }
  });

  const rule = variant.quantity_rule || {};
  const minChunks = rule.min ?? 1;
  const minM = Math.max(1, minChunks * CHUNK);
  let maxM = rule.max != null ? rule.max * CHUNK : 1e9;
  if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue' && variant.inventory_quantity > 0) {
    maxM = Math.min(maxM, variant.inventory_quantity * CHUNK);
  }
  const startM = startMeters(minM, maxM);

  formFabricInputs(form, '.tradeuno-fabric-qty__input').forEach((inp) => {
    inp.setAttribute('min', String(minM));
    inp.setAttribute('max', String(maxM));
    inp.dataset.tradeunoMinChunks = String(minChunks);
    inp.dataset.tradeunoStartMeters = String(startM);
    const cur = parseFloat(inp.value) || startM;
    inp.value = formatMeterDisplay(roundMeters(cur, minM, maxM));
    syncFabricQtyButtons(inp);
    inp.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function bindFabricForm(form) {
  if (form.dataset.tradeunoFabricFormBound) return;
  const wrap = form.closest('[data-tradeuno-fabric-chunks-per-meter]');
  if (wrap?.dataset?.tradeunoFabricChunksPerMeter && !form.dataset.tradeunoFabricChunksPerMeter) {
    form.dataset.tradeunoFabricChunksPerMeter = wrap.dataset.tradeunoFabricChunksPerMeter;
  }
  if (fabricMultiplier(form) <= 1) return;
  form.dataset.tradeunoFabricFormBound = '1';

  form.addEventListener('variant:change', (ev) => {
    const v = ev.detail?.variant;
    queueMicrotask(() => patchFabricPrices(form, v));
  });

  const first = evDetailVariantFromJson(form);
  if (first) queueMicrotask(() => patchFabricPrices(form, first));
}

function evDetailVariantFromJson(form) {
  try {
    const sel = form.querySelector('script[type="application/json"][id^="selected-variant-"]');
    if (!sel?.textContent) return null;
    const data = JSON.parse(sel.textContent.trim());
    if (data && typeof data.id === 'number') return data;
  } catch (_) {}
  return null;
}

function updateTradeunoPdpStockLine(variant) {
  const el = document.querySelector('[data-tradeuno-pdp-stock-line]');
  if (!el || !variant) return;
  const isDec = el.getAttribute('data-template-decimal') === '1';
  const tracked = variant.inventory_management === 'shopify';
  const qty = variant.inventory_quantity ?? 0;
  if (!tracked || qty <= 0) {
    el.setAttribute('hidden', '');
    el.innerHTML = '';
    return;
  }
  el.removeAttribute('hidden');
  let html;
  if (isDec) {
    const meters = formatMeterDisplay(qty * CHUNK);
    html = `Only <span class="hdt-font-semibold">${meters}</span> Meter in stock, ready to be shipped`;
  } else {
    const u = qty === 1 ? 'unit' : 'units';
    html = `Only <span class="hdt-font-semibold">${qty}</span> ${u} in stock, ready to be shipped`;
  }
  el.innerHTML = html;
}

function bindTradeunoPdpStockLine(form) {
  if (form.dataset.tradeunoStockLineBound) return;
  form.dataset.tradeunoStockLineBound = '1';
  form.addEventListener('variant:change', (ev) => {
    updateTradeunoPdpStockLine(ev.detail?.variant);
  });
  const first = evDetailVariantFromJson(form);
  if (first) queueMicrotask(() => updateTradeunoPdpStockLine(first));
}

/**
 * Convert the visible meter value into Shopify quarter-meter units right before the form is
 * serialised, then restore the meter display on the next tick so the PDP keeps reading in meters.
 */
function convertMetersToChunksForSubmit(form) {
  formFabricInputs(form, 'input[data-tradeuno-meter-qty]', 'tradeunoMeterQty').forEach((inp) => {
    if (isFabricCartUpdatesInput(inp)) return;
    // The click-capture and submit-capture listeners both fire for one add-to-cart;
    // guard so we convert meters -> chunks exactly once (otherwise 1.5 -> 6 -> 24).
    if (inp.dataset.tradeunoConverting === '1') return;
    inp.dataset.tradeunoConverting = '1';
    const minChunks = parseInt(inp.dataset.tradeunoMinChunks || '1', 10);
    const meters = parseFloat(inp.value);
    const safeMeters = Number.isNaN(meters) ? minChunks * CHUNK : meters;
    const chunks = metersToChunks(safeMeters, minChunks);
    inp.value = String(chunks);
    setTimeout(() => {
      inp.value = formatMeterDisplay(safeMeters);
      syncFabricQtyButtons(inp);
      delete inp.dataset.tradeunoConverting;
    }, 0);
  });
}

document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    convertMetersToChunksForSubmit(form);
  },
  true
);

/** Safety net: some add-to-cart paths serialise the form on click without firing `submit`. */
document.addEventListener(
  'click',
  (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[type="submit"], [name="add"]') : null;
    if (!btn) return;
    const form = btn.form || btn.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    convertMetersToChunksForSubmit(form);
  },
  true
);

function init() {
  document.querySelectorAll('[data-tradeuno-fabric-qty]').forEach(bindFabricQtyContainer);
  document.querySelectorAll('form.hdt-main-product-form, form.hdt-sticky-atc__form').forEach((form) => {
    bindFabricForm(form);
    bindTradeunoPdpStockLine(form);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
document.addEventListener('cart:update', init);
document.addEventListener('cart:reload', init);
